import React, { useEffect, useState } from 'react'
import { Col, FormGroup, Input, Row, Alert } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import Select from 'react-select'
import {
  plantCheeseAltTypeOptions,
  typeOptions,
  formOptions,
} from './constants'
import { useFormUtils } from 'views/shared/useFormUtils'
import PropTypes from 'prop-types'
import { isNotAlpha } from 'context/Utilities'

const PlantCheesePackageReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({
    fkCategory: item.fkCategory,
    fkSubCategory: item.fkSubCategory,
    PlantCheeseAltType: item.PlantCheeseAltType,
    OtherPlantCheeseAltType: item.OtherPlantCheeseAltType,
    Type: item.Type,
    OtherType: item.OtherType,
    Form: item.Form,
    Size: item.Size,
  })
  const methods = useFormContext()
  const { setSelectReviewer, onChangeReviewer, asterisk } = useFormUtils()

  useEffect(() => {
    if (item) {
      setPackaging({
        PlantCheeseAltType: item.PlantCheeseAltType,
        OtherPlantCheeseAltType: item.OtherPlantCheeseAltType,
        Type: item.Type,
        OtherType: item.OtherType,
        Form: item.Form,
        Size: item.Size,
      })
    }
  }, [item])

  return (
    <Row>
      <Col xs={6}>
        <FormGroup>
          <p>
            <strong>Plant-Based Cheese Alternative Type {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            name="PlantCheeseAltType"
            placeholder={'Select Plant-Based Cheese Alternative Type'}
            options={plantCheeseAltTypeOptions}
            defaultValue={{
              label: packaging.PlantCheeseAltType,
              value: packaging.PlantCheeseAltType,
            }}
            onChange={([selected, value]) => {
              setSelectReviewer(item, selected, value)
              setPackaging(prev => ({
                ...prev,
                PlantCheeseAltType: selected.value,
              }))
              return selected
            }}
            control={methods.control}
            isDisabled={isDisabled}
            rules={{ required: true }}
          />
          <ErrorMessage
            errors={methods.errors}
            name="PlantCheeseAltType"
            message={'⚠ Please select a plant-based Cheese alternative type'}
            as={<Alert color="danger" className="mt-3" />}
          />
        </FormGroup>

        {packaging.PlantCheeseAltType === 'Other' && (
          <FormGroup>
            <p>
              <strong>
                Other Plant-Based Cheese Alternative Type (Please Specify){' '}
                {asterisk}
              </strong>
            </p>
            <Controller
              as={Input}
              name="OtherPlantCheeseAltType"
              defaultValue={packaging.OtherPlantCheeseAltType}
              placeholder={'Other Plant-Based Cheese Alternative Type'}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') e.preventDefault()
              }}
              control={methods.control}
              rules={{
                maxLength: {
                  value: 15,
                  message:
                    '⚠ Other Plant-Based Cheese alternative type exceeds 15 characters.',
                },
                required: {
                  value: true,
                  message:
                    '⚠ Other plant-Based Cheese alternative type is required',
                },
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <ErrorMessage
              errors={methods.errors}
              name="OtherPlantCheeseAltType"
              as={<Alert color="danger" className="mt-3" />}
            />
          </FormGroup>
        )}

        <FormGroup>
          <p>
            <strong>Type {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            name="Type"
            placeholder={'Select Cheese Type'}
            options={typeOptions}
            defaultValue={{ label: packaging.Type, value: packaging.Type }}
            onChange={([selected, value]) => {
              setSelectReviewer(item, selected, value)
              setPackaging(prev => ({ ...prev, Type: selected.value }))
              return selected
            }}
            control={methods.control}
            isDisabled={isDisabled}
            rules={{ required: true }}
          />
          <ErrorMessage
            errors={methods.errors}
            name="Type"
            message={'⚠ Please select a cheese type'}
            as={<Alert color="danger" className="mt-3" />}
          />
        </FormGroup>

        {packaging.Type === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="OtherType"
              defaultValue={packaging.OtherType}
              placeholder={'Other Type'}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') e.preventDefault()
              }}
              control={methods.control}
              rules={{
                maxLength: {
                  value: 15,
                  message: '⚠ Other type exceeds 15 characters.',
                },
                required: { value: true, message: '⚠ Other type is required' },
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <ErrorMessage
              errors={methods.errors}
              name="OtherType"
              as={<Alert color="danger" className="mt-3" />}
            />
          </FormGroup>
        )}
      </Col>

      <Col xs={6}>
        <FormGroup>
          <p>
            <strong>Form {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            name="Form"
            placeholder={'Select Form'}
            options={formOptions}
            defaultValue={{ label: packaging.Form, value: packaging.Form }}
            onChange={([selected, value]) => {
              setSelectReviewer(item, selected, value)
              return selected
            }}
            control={methods.control}
            isDisabled={isDisabled}
            rules={{ required: true }}
          />
          <ErrorMessage
            errors={methods.errors}
            name="Form"
            message={'⚠ Please select a form'}
            as={<Alert color="danger" className="mt-3" />}
          />
        </FormGroup>

        <FormGroup>
          <p>
            <strong>Size {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            name="Size"
            defaultValue={packaging.Size}
            control={methods.control}
            rules={{
              maxLength: { value: 5, message: '⚠ Size exceeds 5 characters' },
              required: { value: true, message: '⚠ Please enter product size' },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onKeyDown={e => {
              if (
                ![
                  'Backspace',
                  'Delete',
                  'Tab',
                  'ArrowLeft',
                  'ArrowRight',
                ].includes(e.key) &&
                isNaN(parseInt(e.key)) &&
                e.key !== '.'
              ) {
                e.preventDefault()
              }
            }}
            onChange={([event]) => {
              onChangeReviewer(item, event)
              return event
            }}
            disabled={isDisabled}
          />
          <ErrorMessage
            errors={methods.errors}
            name="Size"
            as={<Alert color="danger" className="mt-3" />}
          />
        </FormGroup>
      </Col>
    </Row>
  )
}

PlantCheesePackageReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default PlantCheesePackageReview
